/**
 * Module-quiz component (FR-E — Sprint 3 client side).
 *
 * Mirrors ``KnowledgeCheck`` but with module-quiz semantics:
 *
 * - 10 items per attempt (vs. 5 for lesson KCs).
 * - DEC-0012: a 422 from /start with ``blocking=cooldown_active`` renders
 *   a countdown banner instead of the quiz; the submit response can also
 *   set ``cooldown_until`` on the 3rd consecutive failure.
 * - DEC-0014: on a passing submit, the parent (``ModuleQuizPage``)
 *   navigates to the reflection prompt instead of "Continue". The
 *   component still owns the "Try again" path on a failing submit.
 *
 * State machine:
 *
 *   idle ──[Start]──▶ attempting ──[Submit]──▶ reviewing ──[Continue]──▶ done
 *                                                                  │
 *                                                                  └─[Pass]──▶ onPassed()
 *
 * Cooldown is a sibling state to idle (handled in the same render branch):
 * if ``startModuleQuiz`` throws ``ModuleQuizCooldownError``, we render the
 * countdown UI from local state and let the user retry once the time
 * passes (an interval refreshes the displayed remaining time).
 */
import * as React from "react";
import { useCallback, useEffect, useState } from "react";

import type {
  KCClientItem,
  SubmitReviewItem,
} from "@/lib/lesson-api";
import {
  ModuleQuizCooldownError,
  recordModuleQuizAnswer,
  startModuleQuiz,
  submitModuleQuiz,
  type SubmitModuleQuizResponse,
} from "@/lib/module-api";

type ModuleQuizState =
  | { kind: "idle"; error: string | null }
  | { kind: "cooldown"; until: Date; consecutive: number; cap: number }
  | {
      kind: "attempting";
      attemptId: string;
      items: KCClientItem[];
      passingPercent: number;
      attemptNumber: number;
      answers: Record<string, string>;
      submitting: boolean;
      error: string | null;
    }
  | { kind: "reviewing"; response: SubmitModuleQuizResponse }
  | { kind: "starting" };

export interface ModuleQuizProps {
  moduleId: string;
  /** Fires when the candidate passes — parent renders ReflectionPrompt. */
  onPassed: (response: SubmitModuleQuizResponse) => void;
  /** Test seams. */
  startFn?: typeof startModuleQuiz;
  answerFn?: typeof recordModuleQuizAnswer;
  submitFn?: typeof submitModuleQuiz;
}

function formatCountdown(target: Date): string {
  const ms = target.getTime() - Date.now();
  if (ms <= 0) return "Ready";
  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

export function ModuleQuiz({
  moduleId,
  onPassed,
  startFn = startModuleQuiz,
  answerFn = recordModuleQuizAnswer,
  submitFn = submitModuleQuiz,
}: ModuleQuizProps): React.JSX.Element {
  const [state, setState] = useState<ModuleQuizState>({
    kind: "idle",
    error: null,
  });

  // Tick every second when in cooldown so the countdown updates.
  const [, setNowTick] = useState(0);
  useEffect(() => {
    if (state.kind !== "cooldown") return undefined;
    const id = window.setInterval(() => setNowTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, [state.kind]);

  // When cooldown elapses, drop back to idle. Depends only on the
  // cooldown target — re-runs each second via the parent tick.
  const cooldownUntilMs =
    state.kind === "cooldown" ? state.until.getTime() : null;
  useEffect(() => {
    if (cooldownUntilMs === null) return;
    if (cooldownUntilMs <= Date.now()) {
      setState({ kind: "idle", error: null });
    }
  }, [cooldownUntilMs]);

  const onStart = useCallback(async (): Promise<void> => {
    setState({ kind: "starting" });
    try {
      const r = await startFn(moduleId);
      setState({
        kind: "attempting",
        attemptId: r.attempt_id,
        items: r.items,
        passingPercent: r.passing_percent,
        attemptNumber: r.attempt_number,
        answers: {},
        submitting: false,
        error: null,
      });
    } catch (err) {
      if (err instanceof ModuleQuizCooldownError) {
        setState({
          kind: "cooldown",
          until: err.cooldown_until,
          consecutive: err.consecutive_failed_attempts,
          cap: err.failure_cap,
        });
        return;
      }
      const msg = err instanceof Error ? err.message : "Could not start.";
      setState({ kind: "idle", error: msg });
    }
  }, [moduleId, startFn]);

  const onAnswer = useCallback(
    async (itemId: string, optionId: string): Promise<void> => {
      // Optimistic local update + fire-and-forget POST.
      //
      // Read attemptId from the closure (not from inside the setState
      // updater): React 18's automatic batching means the updater
      // callback runs in a later microtask, so a side-effect like
      // ``attemptId = prev.attemptId`` inside the updater hasn't
      // happened yet by the time the next line of this function runs.
      // That bug made every recorded answer silently no-op and every
      // module-quiz attempt grade as 0%. KC's onAnswer in
      // KnowledgeCheck.tsx uses this same closure pattern.
      const attemptId = state.kind === "attempting" ? state.attemptId : "";
      setState((prev) => {
        if (prev.kind !== "attempting") return prev;
        return {
          ...prev,
          answers: { ...prev.answers, [itemId]: optionId },
        };
      });
      if (!attemptId) return;
      try {
        await answerFn(attemptId, itemId, optionId);
      } catch {
        // Ignored — server reconciles via last-write-wins on submit.
      }
    },
    [answerFn, state],
  );

  const onSubmit = useCallback(async (): Promise<void> => {
    if (state.kind !== "attempting") return;
    setState({ ...state, submitting: true });
    try {
      const r = await submitFn(state.attemptId);
      setState({ kind: "reviewing", response: r });
      if (r.passed) {
        onPassed(r);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Could not submit.";
      setState({ ...state, submitting: false, error: msg });
    }
  }, [state, submitFn, onPassed]);

  const onTryAgain = useCallback((): void => {
    if (state.kind !== "reviewing") return;
    // If this attempt triggered a cooldown, jump straight to cooldown view.
    if (state.response.cooldown_until !== null) {
      setState({
        kind: "cooldown",
        until: new Date(state.response.cooldown_until),
        // The submit response doesn't echo back the cap — hardcode 3
        // to match DEC-0012 (the spec constant).
        consecutive: 3,
        cap: 3,
      });
      return;
    }
    setState({ kind: "idle", error: null });
  }, [state]);

  // ----- Render -----

  if (state.kind === "idle" || state.kind === "starting") {
    return (
      <section className="rounded-lg border border-border bg-white p-6">
        <h2 className="text-lg font-semibold text-foreground">Module quiz</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          10 multiple-choice questions drawn from this module's lesson pool.
          You need 80% (8 of 10 correct) to pass and unlock the next module.
          Three failed attempts in a row trigger a 1-hour cooldown.
        </p>
        {state.kind === "idle" && state.error !== null ? (
          <p className="mt-3 text-sm text-red-700" role="alert">
            {state.error}
          </p>
        ) : null}
        <button
          type="button"
          onClick={() => {
            void onStart();
          }}
          disabled={state.kind === "starting"}
          className="mt-5 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
        >
          {state.kind === "starting" ? "Starting…" : "Start module quiz"}
        </button>
      </section>
    );
  }

  if (state.kind === "cooldown") {
    return (
      <section
        className="rounded-lg border border-amber-300 bg-amber-50 p-6"
        data-testid="cooldown-banner"
      >
        <h2 className="text-lg font-semibold text-foreground">
          Module quiz cooldown
        </h2>
        <p className="mt-2 text-sm text-foreground">
          You've used all {state.cap} attempts in this cycle. Take some time
          to review the module material — the quiz unlocks again in{" "}
          <span className="font-mono font-semibold">
            {formatCountdown(state.until)}
          </span>
          .
        </p>
        <p className="mt-2 text-xs text-muted-foreground">
          Unlocks at {state.until.toLocaleString()}.
        </p>
      </section>
    );
  }

  if (state.kind === "attempting") {
    const allAnswered = state.items.every((item) =>
      Object.hasOwn(state.answers, item.id),
    );
    return (
      <section className="rounded-lg border border-border bg-white p-6">
        <h2 className="text-lg font-semibold text-foreground">
          Module quiz — attempt {state.attemptNumber} (
          {Object.keys(state.answers).length}/{state.items.length} answered)
        </h2>
        <ol className="mt-4 space-y-8">
          {state.items.map((item, idx) => (
            <li key={item.id}>
              <p className="text-sm font-semibold text-foreground">
                {idx + 1}. {item.stem}
              </p>
              <fieldset className="mt-3 space-y-2">
                <legend className="sr-only">
                  Options for question {idx + 1}
                </legend>
                {item.options.map((opt) => {
                  const selected = state.answers[item.id] === opt.id;
                  return (
                    <label
                      key={opt.id}
                      className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 text-sm ${
                        selected
                          ? "border-primary bg-background"
                          : "border-border hover:bg-muted"
                      }`}
                    >
                      <input
                        type="radio"
                        name={item.id}
                        value={opt.id}
                        checked={selected}
                        onChange={() => {
                          void onAnswer(item.id, opt.id);
                        }}
                        className="mt-0.5"
                      />
                      <span className="flex-1">
                        <span className="font-mono text-xs uppercase text-muted-foreground">
                          {opt.id}.
                        </span>{" "}
                        {opt.text}
                      </span>
                    </label>
                  );
                })}
              </fieldset>
            </li>
          ))}
        </ol>
        {state.error !== null ? (
          <p className="mt-3 text-sm text-red-700" role="alert">
            {state.error}
          </p>
        ) : null}
        <button
          type="button"
          onClick={() => {
            void onSubmit();
          }}
          disabled={!allAnswered || state.submitting}
          className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
        >
          {state.submitting ? "Grading…" : "Submit module quiz"}
        </button>
      </section>
    );
  }

  // state.kind === "reviewing"
  const { response } = state;
  if (response.passed) {
    // Passing review: parent has rendered ReflectionPrompt; show a brief
    // banner here so the page doesn't look empty during the handoff.
    return (
      <section
        className="rounded-lg border border-emerald-200 bg-emerald-50 p-6"
        data-testid="quiz-pass-banner"
      >
        <h2 className="text-lg font-semibold text-foreground">
          Passed — {response.score}%
        </h2>
        <p className="mt-2 text-sm text-foreground">
          You've cleared the {response.passing_percent}% threshold for this
          module. One more step: take a moment to reflect below.
        </p>
      </section>
    );
  }
  // Failing review: show rationales + Try again. ``items`` should always
  // be present when ``passed: false`` but guard so a malformed response
  // can't crash the review tree.
  const reviewItems: SubmitReviewItem[] = Array.isArray(response.items)
    ? response.items
    : [];
  return (
    <section className="rounded-lg border border-amber-200 bg-amber-50 p-6">
      <h2 className="text-lg font-semibold text-foreground">
        Not quite — {response.score}%
      </h2>
      <p className="mt-2 text-sm text-foreground">
        Threshold: {response.passing_percent}%. Review each question below,
        then try again.
      </p>
      <ol className="mt-5 space-y-6">
        {reviewItems.map((item: SubmitReviewItem, idx: number) => (
          <li key={item.id} className="rounded-md bg-white p-4">
            <p className="text-sm font-semibold text-foreground">
              {idx + 1}. {item.stem}
            </p>
            <ul className="mt-2 space-y-1 text-sm">
              {item.options.map((opt) => {
                const isCandidate = opt.id === item.candidate_option_id;
                const isCorrect = opt.id === item.correct_option_id;
                return (
                  <li
                    key={opt.id}
                    className={`rounded px-2 py-1 ${
                      isCorrect
                        ? "bg-emerald-100 font-semibold text-emerald-900"
                        : isCandidate
                          ? "bg-red-100 text-red-900"
                          : "text-foreground"
                    }`}
                  >
                    <span className="font-mono text-xs uppercase text-muted-foreground">
                      {opt.id}.
                    </span>{" "}
                    {opt.text}
                  </li>
                );
              })}
            </ul>
            <p className="mt-3 text-sm text-foreground">
              <span className="font-semibold">Why:</span>{" "}
              {
                item.options.find(
                  (opt) => opt.id === item.correct_option_id,
                )?.rationale
              }
            </p>
          </li>
        ))}
      </ol>
      <button
        type="button"
        onClick={onTryAgain}
        className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
      >
        Try again
      </button>
    </section>
  );
}
